import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Checkbox,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import { UserProfileSearchFragment } from 'api';
import { UserAvatar } from 'components';

import { Numeric, TableCellWithOverflow } from 'components';
import { generateLink } from 'Links';

import Loader from './Loader';

interface IProps {
  sentinelRef: (node: Element) => void;
  results: UserProfileSearchFragment[];
  checkedIds: string[];
  isAllChecked: boolean;
  isExhausted: boolean;
  isInitialLoad: boolean;
  onAllChecked: (event: ChangeEvent<HTMLInputElement>) => void;
  onChecked: (event: ChangeEvent<HTMLInputElement>) => void;
}

const NAME_CELL_WIDTH = 400;

const AdminPeopleTable: FC<IProps> = ({
  sentinelRef,
  results,
  checkedIds,
  isAllChecked,
  isExhausted,
  isInitialLoad,
  onAllChecked,
  onChecked,
}) => {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox checked={isAllChecked} onChange={onAllChecked} />
          </TableCell>
          <TableCell style={{ width: NAME_CELL_WIDTH }}>{t('common:name')}</TableCell>
          <TableCell>{t('common:title')}</TableCell>
          <TableCell>{t('common:department')}</TableCell>
          <TableCell>{t('common:role')}</TableCell>
          <TableCell>{t('common:reach')}</TableCell>
          <TableCell>{t('admin:lastActive')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isInitialLoad && <Loader sentinelRef={null} rows={8} />}
        {results.map(result => (
          <TableRow key={result.id}>
            <TableCell padding="checkbox">
              <Checkbox
                name={result.id}
                checked={checkedIds.includes(result.id)}
                onChange={onChecked}
              />
            </TableCell>
            <TableCellWithOverflow>
              <Grid container alignItems="center" wrap="nowrap" spacing={2}>
                <Grid item>
                  <UserAvatar size={50} link user={result as any} />
                </Grid>
                <Grid item container zeroMinWidth>
                  <Link
                    title={result.full_name}
                    component={generateLink(`/profile/${result.id}`)}
                    variant="subtitle2"
                    noWrap
                  >
                    {result.full_name}
                  </Link>
                </Grid>
              </Grid>
            </TableCellWithOverflow>
            <TableCell>
              <Typography noWrap title={result.job_title}>
                {result.job_title}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography noWrap title={result.department_title}>
                {result.department_title}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography noWrap title={result.user_type}>
                {result.user_type}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography noWrap>
                <Numeric value={result.follower_count} />
              </Typography>
            </TableCell>
            <TableCell>
              <Typography noWrap>
                {result.last_accessed_at ? moment.unix(result.last_accessed_at).format('l') : '--'}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
        {!isExhausted && <Loader sentinelRef={sentinelRef} rows={1} />}
      </TableBody>
    </Table>
  );
};

AdminPeopleTable.defaultProps = {
  results: [],
};

export default AdminPeopleTable;
